import { autocapturePlugin } from '@amplitude/plugin-autocapture-browser';
import * as amplitudeSessionReplayPlugin from '@amplitude/plugin-session-replay-browser';
import { GoogleTagManager, tracking } from '@iwoca/frontend-tracking-library';
import _ from 'lodash';
import * as queryStringHelpers from 'qs';
import { stringify } from 'qs';
import { getValues } from 'redux-form';
import { delay } from 'redux-saga';
import {
    all,
    call,
    put,
    fork,
    take,
    cancel,
    select,
    takeLatest,
    takeEvery
} from 'redux-saga/effects';

import { generateApplicationPayloadFromGetRequest } from '@lendingAPI/helpers/ApplicationAPIHelper';
import { postSaveUploadedDocuments } from '@lendingAPI/lendingAPI';
import {
    SET_IMPERSONATING_STATE_KEY,
    SET_CUSTOMER_STATUS
} from '@lendingAPI/lendingAPI.actions';
import lendingAPISagas from '@lendingAPI/lendingAPI.sagas';
import { putAPI } from '@lendingAPI/lendingAPIHelpers';
import {
    getApplicantEmail,
    getCountryCode,
    getRequestedProductType,
    getStateKey,
    missingRequiredApplicationDetails as missingRequiredApplicationDetailsSelector
} from '@lendingAPI/lendingAPISelector';
import * as constants from '@lendingAPI/productTypes.constants';
import {
    convertActiveStepIdToStepPathName,
    BUSINESS_ACCOUNTS_STEP_ID,
    PERSONAL_INFORMATION_STEP_ID,
    BUSINESS_INTRODUCTION_STEP_ID
} from '@src/containers/Routes/helper/stepActiveIds.helper';
import { navigateTo } from '@src/containers/Routes/Navigation.action';

import autoDirectorsSagas from './components/FullName/limitedCompany/AutoDirectors.sagas';
import { requestALoginLinkWorker } from './components/RequestLoginLinkMessage/RequestLoginLinkMessage.sagas';
import SaveApplicationSagas from './components/SaveApplication/SaveApplication.sagas';
import ThreatMetrixSagas from './components/ThreatMatrix/ThreatMetrix.sagas';
import TrueLayerSagas from './components/TrueLayer/TrueLayer.sagas';
import { CHECK_ACCOUNT_CREATED } from './containers/AccountCreatedNotification/AccountCreatedNotification.actions';
import accountCreatedSagas from './containers/AccountCreatedNotification/AccountCreatedNotification.sagas';
import additionalGuarantorSagas from './containers/AdditionalGuarantor/AdditionalGuarantor.sagas';
import automatedLendingDecisionSagas from './containers/AutomatedLendingDecision/AutomatedLendingDecision.sagas';
import exitPollSagas from './containers/AutomatedLendingDecision/ExitPoll/ExitPoll.sagas';
import BusinessIntroductionSagas from './containers/BusinessIntroduction/BusinessIntroduction.sagas';
import businessIntroductionDESagas from './containers/BusinessIntroductionDE/BusinessIntroductionDE.sagas';
import checkEligibilitySagas from './containers/CheckEligibility/CheckEligibility.sagas';
import { GET_LOAN_DECISION } from './containers/LoanDecision/LoanDecision.actions';
import loanDecisionSagas, {
    ACCOUNT_PAGES_URL
} from './containers/LoanDecision/LoanDecision.sagas';
import * as selectors from './containers/LoanDecision/LoanDecision.selectors';
import LoanRequestDetailsSagas from './containers/LoanRequestDetails/LoanRequestDetails.sagas';
import { OPEN_MODAL } from './containers/Modals/Modals.actions';
import modalsSagas from './containers/Modals/Modals.sagas';
import OffersSagas from './containers/Offers/offers/Offers.sagas.js';
import { isNewOffersEnabled } from './containers/Offers/offers/offersHelpers';
import { watchNavigation } from './containers/Routes/Navigation.saga.ts';
import { getBasePath } from './containers/Routes/Navigation.selector';
import { amplitudeDocUploadEvent } from './containers/TradingHistoryV2/TradingHistory.amplitude';
import TradingHistorySagas from './containers/TradingHistoryV2/TradingHistory.sagas';
import { buildApplicationState } from './helpers/applicationManager';
import { isSignupComplete } from './helpers/completedApplication';
import { getQueryParams } from './helpers/helpers';
import { mergeWithApplicationState } from './helpers/pageTagHelper';
import { redirectToURL } from './helpers/redirection';
import monitoringSagas from './loggers/Monitoring.sagas';
import { STEP_ZERO_STEP_ID } from './route-old';
import { AmplitudeDataDogPlugin } from './tracking/AmplitudeDataDogPlugin';
import {
    AMPLITUDE_USER_PROPERTIES,
    getAmplitudeDeviceCategory,
    getAmplitudeDomainCountry,
    getAmplitudeSplitTestsFromContext
} from './tracking/amplitudeEvents';
import IwTracking from './tracking/IwTracking';
import { iwTrackingSagas } from './tracking/IwTracking.sagas';
import * as API from './utils/Api';
import { isIwocaDE } from './utils/countryHelper';
import minimumDataRequirement from './utils/MinimumDataRequirement';

export function* fetchInitialApplicationState(action) {
    try {
        yield put({ type: 'INITIAL_APP_START' });

        const queryParams = queryStringHelpers.parse(
            window.location.search.substring(1)
        );

        if (queryParams.state_key) {
            yield put({
                type: SET_IMPERSONATING_STATE_KEY,
                stateKey: queryParams.state_key
            });
        }

        yield put({ type: 'api/GET_PROFILE' });

        const profile = yield take([
            'api/GET_PROFILE [success]',
            'api/GET_PROFILE [fail]'
        ]);

        // Redirect partner customers to their own signup
        if (profile?.payload?.signup_affiliate_tag) {
            redirectToURL(
                `/partner-signup/${profile.payload.signup_affiliate_tag}`
            );
        }

        yield put({ type: 'api/GET_CONTEXT' });

        const context = yield take([
            'api/GET_CONTEXT [success]',
            'api/GET_CONTEXT [fail]'
        ]);

        const isDE = isIwocaDE();

        const countryCode = context?.payload?.country_code
            ? context.payload.country_code
            : isDE
            ? 'de'
            : 'uk';

        API.setCountryCodeSingleton(countryCode); // Needed to support legacy version of singelton countrycode

        yield all([
            put({ type: 'api/GET_APPLICATION' }),
            put({ type: 'api/GET_CUSTOMER_STATE' })
        ]);

        const { customerStateResponse, applicationResponse } = yield all({
            customerStateResponse: take([
                'api/GET_CUSTOMER_STATE [success]',
                'api/GET_CUSTOMER_STATE [fail]'
            ]),
            applicationResponse: take([
                'api/GET_APPLICATION [success]',
                'api/GET_APPLICATION [fail]'
            ])
        });

        const customerStatus =
            profile?.type === 'api/GET_PROFILE [success]' &&
            context?.type === 'api/GET_CONTEXT [success]' &&
            customerStateResponse?.type ===
                'api/GET_CUSTOMER_STATE [success]' &&
            applicationResponse?.type === 'api/GET_APPLICATION [success]' &&
            context?.payload?.state_key
                ? 'applicant'
                : 'new';

        yield put({ type: SET_CUSTOMER_STATUS, customerStatus });

        if (customerStatus === 'new' && countryCode === 'uk') {
            const stepZeroPathName = convertActiveStepIdToStepPathName(
                STEP_ZERO_STEP_ID,
                isUK
            );

            yield put({
                type: 'SELECT_STEP',
                selectedStep: { id: STEP_ZERO_STEP_ID }
            });

            yield put({ type: 'INITIAL_APP_END' });
            return yield put(
                navigateTo(
                    `${basePath}/${stepZeroPathName}${
                        urlQueryParams ? `?${urlQueryParams}` : ''
                    }`
                )
            );
        }

        yield call(
            setInitialApplicationAmplitudeUserProperties,
            context,
            profile,
            applicationResponse
        );

        const stateSnapshot = yield select();
        const isUK = countryCode === 'uk';
        const urlQueryParams = stringify(getQueryParams());
        const basePath = getBasePath(stateSnapshot);

        const businessIntroductionPathname = convertActiveStepIdToStepPathName(
            BUSINESS_INTRODUCTION_STEP_ID,
            isUK
        );

        const mergedCustomerState = mergeWithApplicationState(
            context.payload.page_tags,
            customerStateResponse.payload
        );

        redirectIfSignupComplete({
            siteContext: context.payload
        });

        if (
            profile?.payload.user_type === 'staff' &&
            applicationResponse.payload?.status === 'deferred'
        ) {
            return yield put({
                type: OPEN_MODAL,
                modalId: 'deferredModal'
            });
        } else {
            yield createNewApplicationIfDeferred(applicationResponse.payload);
        }

        const missingRequiredApplicationDetails = yield select(
            missingRequiredApplicationDetailsSelector
        );

        if (missingRequiredApplicationDetails) {
            // tested in ApplicationInfo.spec.js
            yield put({
                type: OPEN_MODAL,
                modalId: 'sidebarLoanRequestSummary'
            });
        }

        yield call(updateSplitTestAttributes);

        const stateKey = yield select(getStateKey);

        const requestedProductType = yield select(getRequestedProductType);

        IwTracking.setUserIdentifiers({
            stateKey,
            requestedProductType
        });

        if (requestedProductType === constants.IWOCAPAY_PRODUCT_TYPE) {
            if (!isSignupComplete(context.payload)) {
                return redirectToURL('/my_account/'); // go to iwocaPay signup
            }
        }

        yield put({
            type: 'APPLICATION_STATE_FETCH_SUCCEED',
            payload: {
                application: mergedCustomerState.application,
                countryCode
            }
        });

        yield put({
            type: CHECK_ACCOUNT_CREATED,
            payload: { customerState: mergedCustomerState }
        });

        yield put({
            type: 'UI_STATE_FETCH_SUCCEED',
            ui: mergedCustomerState.ui,
            countryCode
        });

        const applicantEmail = yield select((state) =>
            getApplicantEmail(state)
        );

        GoogleTagManager.track('addedApplicantEmailToUser', {
            email: applicantEmail
        });

        const { state } = yield select((state) => ({
            state
        }));

        const stateMinimumDataRequirements = yield call(
            minimumDataRequirement,
            state
        );

        const currentStep = selectors.getCurrentStep(state);

        const personalInformationPathname = convertActiveStepIdToStepPathName(
            PERSONAL_INFORMATION_STEP_ID,
            isUK
        );

        const businessAccountsPathname = convertActiveStepIdToStepPathName(
            BUSINESS_ACCOUNTS_STEP_ID,
            isUK
        );

        switch (stateMinimumDataRequirements) {
            case 'complete_min_requirement':
                // Requires the user to go to step 2 first

                if (
                    currentStep === BUSINESS_ACCOUNTS_STEP_ID &&
                    // TODO: Check if we want people to go to product offers every time even after linking OB & check DE
                    !isNewOffersEnabled(state) &&
                    getCountryCode(state) === 'uk'
                ) {
                    yield put({
                        type: 'SELECT_STEP',
                        selectedStep: { id: BUSINESS_ACCOUNTS_STEP_ID }
                    });

                    yield put(
                        navigateTo(
                            `${basePath}/${businessAccountsPathname}${
                                urlQueryParams ? `?${urlQueryParams}` : ''
                            }`
                        )
                    );
                } else {
                    yield put({
                        type: 'SELECT_STEP',
                        selectedStep: { id: PERSONAL_INFORMATION_STEP_ID }
                    });

                    yield put(
                        navigateTo(
                            `${basePath}/${personalInformationPathname}${
                                urlQueryParams ? `?${urlQueryParams}` : ''
                            }`
                        )
                    );

                    yield put({
                        type: GET_LOAN_DECISION
                    });
                }

                break;
            case 'incomplete_privacy_policy_de':
                yield put({
                    type: 'SELECT_STEP',
                    selectedStep: { id: PERSONAL_INFORMATION_STEP_ID }
                });

                yield put(
                    navigateTo(
                        `${basePath}/${personalInformationPathname}${
                            urlQueryParams ? `?${urlQueryParams}` : ''
                        }`
                    )
                );

                yield put({
                    type: OPEN_MODAL,
                    modalId: 'creditCheckConsent'
                });

                break;
            case 'incomplete_personal_info':
                yield put({
                    type: 'SELECT_STEP',
                    selectedStep: { id: PERSONAL_INFORMATION_STEP_ID }
                });

                yield put(
                    navigateTo(
                        `${basePath}/${personalInformationPathname}${
                            urlQueryParams ? `?${urlQueryParams}` : ''
                        }`
                    )
                );
                break;
            case 'incomplete_company_info':
            default:
                IwTracking.completeSignupStep('stepZero');
                yield put(IwTracking.trackLeadQualityAction('stepZero'));

                yield put({
                    type: 'SELECT_STEP',
                    selectedStep: { id: BUSINESS_INTRODUCTION_STEP_ID }
                });

                yield put(
                    navigateTo(
                        `${basePath}/${businessIntroductionPathname}${
                            urlQueryParams ? `?${urlQueryParams}` : ''
                        }`
                    )
                );

                break;
        }
    } catch (e) {
        if (import.meta.env.NODE_ENV === 'production') {
            yield put({
                type: 'APPLICATION_STATE_FETCH_FAILED',
                payload: {
                    code: e.message || e.response.status,
                    failedAction: action.type
                }
            });
        }
    }

    yield put({ type: 'INITIAL_APP_END' });
}

export function* updateSplitTestAttributes() {
    yield put({ type: 'api/GET_CONTEXT' });

    yield take('api/GET_CONTEXT [success]');

    const fetchContext = yield select((state) => state.lendingAPI.context.data);

    const attributes = {
        ...fetchContext.user_split_tests.reduce(
            (contextSplitTests, splitTest) => ({
                ...contextSplitTests,
                ...splitTest
            }),
            {}
        ),
        ...fetchContext.introducer_account_split_tests.reduce(
            (contextSplitTests, splitTest) => ({
                ...contextSplitTests,
                ...splitTest
            }),
            {}
        )
    };

    tracking.ampli.identify(fetchContext?.state_key, {
        'Split tests': getAmplitudeSplitTestsFromContext(fetchContext)
    });

    yield put({ type: 'UPDATE_ATTRIBUTES', attributes });
}

function* createNewApplicationIfDeferred(application) {
    if (application?.status !== 'deferred') return;

    const putApplicationPayload =
        generateApplicationPayloadFromGetRequest(application);

    if (!putApplicationPayload) return;

    yield put({
        type: 'api/PUT_APPLICATION',
        application: putApplicationPayload
    });
    yield take('api/PUT_APPLICATION [success]');
    yield putAPI({
        type: 'api/GET_APPLICATION'
    });
}

/**
 * Polls Intercom attributes every 10 seconds to check if the data
 * is there yet. Once data received polling stops. This endpoint is based on IntercomAccount model which is created on
 * first sync to intercom. Sync is done asynchronously and can take up to a minute(if celery queue is not overloaded).
 */
function* pollIntercomAttributes() {
    const isInTest = import.meta.env.NODE_ENV === 'test';

    const queryParams = queryStringHelpers.parse(
        window.location.search.substring(1)
    );
    while (!queryParams.state_key && !isInTest) {
        try {
            const intercomAttributes = yield call(API.fetchIntercomAttributes);
            if (intercomAttributes) {
                yield put({
                    type: 'UPDATE_INTERCOM_ATTRIBUTES',
                    attributes: intercomAttributes
                });
                yield put({ type: 'CANCEL_INTERCOM_POLLING' });
            }
            yield call(delay, 10000);
        } catch (e) {
            yield put({ type: 'CANCEL_INTERCOM_POLLING' });
        }
    }
}

function* fetchIntercomAttributes() {
    const task = yield fork(pollIntercomAttributes);
    yield take('CANCEL_INTERCOM_POLLING');
    // waits for the action to cancel the task
    yield cancel(task);
}

export function* fetchCompaniesLookup(action) {
    try {
        yield call(delay, 250);
        const companies = yield call(
            API.fetchCompaniesLookup,
            action.companyName,
            null,
            API.extractValuesFromEnglishCompanySearchAPIResponse
        );
        yield put({ type: 'COMPANIES_FETCH_SUCCEED', companies });
    } catch (e) {
        yield put({ type: 'COMPANIES_FETCH_FAILED', message: e.message });
    }
}

export function* saveNewCustomerState({ step }) {
    const { application, formValues, ui } = yield select((state) => ({
        ui: state.ui,
        application: state.fetched.application.payload,
        formValues: getValues(state.form.sign_up)
    }));
    const countryCode = yield select((state) => getCountryCode(state));

    const applicationState = buildApplicationState(application, formValues);
    const stateToSave = {
        data: {
            application: applicationState,
            ui: _.merge({}, ui, {
                activeStep: _.find(ui.steps, { id: step })
            })
        }
    };

    const state = yield call(API.putNewState, stateToSave, countryCode);
    yield put({ type: 'SAVE_CUSTOMER_STATE_SUCCEED', state });
}

export function* fetchAddressLookup(action) {
    try {
        yield call(delay, 250);
        const addresses = yield call(API.fetchAddressLookup, action.postcode);
        yield put({ type: 'ADDRESSES_FETCH_SUCCEED', addresses });
    } catch (e) {
        yield put({ type: 'ADDRESSES_FETCH_FAILED', message: e.message });
    }
}

export function* saveCustomerState(action) {
    try {
        const exitPollAnswer = yield select((state) => state.exitPoll.value);
        if (exitPollAnswer) {
            const applicant = _.find(
                action.state.data.application.people,
                (person) => _.includes(person.roles, 'applicant')
            );
            applicant.how_did_you_find_us = exitPollAnswer;
        }

        const countryCode = yield select(getCountryCode);
        const state = yield call(API.putNewState, action.state, countryCode);
        yield put({ type: 'SAVE_CUSTOMER_STATE_SUCCEED', state });
    } catch (e) {
        yield put({
            type: 'SAVE_CUSTOMER_FAILED',
            payload: {
                code: e.response.status,
                failedAction: action.type
            }
        });
    }
}

function* uploadSaga(action) {
    try {
        const body = new FormData();
        body.append('document', action.file);
        body.append('document_type', action.documentType);
        const stateKey = yield select(
            ({ lendingAPI }) =>
                lendingAPI.impersonatingStateKey || lendingAPI.stateKey
        );
        yield call(postSaveUploadedDocuments, stateKey, body);
        yield put({ type: 'FILE_UPLOAD_SUCCESS', id: action.file.id });
        amplitudeDocUploadEvent(action.documentType);
        const callbackOnSuccess = action.callbackOnSuccess;
        if (callbackOnSuccess) {
            yield call(callbackOnSuccess);
        }
    } catch (error) {
        yield put({
            type: 'FILE_UPLOAD_FAILED',
            error: {
                id: action.file.id,
                message: error
            }
        });
    }
}

export function* linkAccount(action) {
    try {
        yield call(API.linkAccount, action.data);
        yield put({ type: 'ACCOUNT_LINKING_SUCCEED' });
    } catch (e) {
        const response = yield call(() => e.response.json());
        yield put({ type: 'ACCOUNT_LINKING_FAILED', errors: response.errors });
    }
}

export function* checkAdditionalGuarantors() {
    const payload = yield call(API.fetchAdditionalGuarantorStatus);
    yield put({ type: 'CHECK_ADDITIONAL_GUARANTORS_SUCCEED', payload });
}

export function* getApplicationState() {
    const { application, formValues } = yield select((state) => ({
        application: state.fetched.application.payload,
        formValues: getValues(state.form.sign_up)
    }));

    return buildApplicationState(application, formValues);
}

function redirectIfSignupComplete({ siteContext }) {
    if (!isSignupComplete(siteContext)) return;

    return redirectToURL(ACCOUNT_PAGES_URL);
}

async function setInitialApplicationAmplitudeUserProperties(
    contextData,
    profileData,
    applicationData
) {
    const stateKey = contextData?.payload?.state_key;
    const userType = profileData?.payload?.user_type;

    if (
        (userType === 'customer' || userType === 'introducer_customer') &&
        !profileData?.payload?.impersonator_type
    ) {
        window.DD_RUM &&
            window.DD_RUM.setUser({
                state_key: stateKey
            });

        tracking.ampli.client.add({
            userId: contextData?.payload?.state_key
        });

        if (
            import.meta.env.NODE_ENV !== 'test' &&
            import.meta.env.NODE_ENV !== undefined
        ) {
            await tracking.ampli.client.add(
                amplitudeSessionReplayPlugin.sessionReplayPlugin({
                    sampleRate: 1
                })
            ).promise;
        }

        tracking.ampli.client.add(new AmplitudeDataDogPlugin());
        tracking.ampli.client.add(autocapturePlugin());

        tracking.ampli.identify(stateKey, {
            Project: 'Direct signup',
            'Domain country': getAmplitudeDomainCountry(),
            'Device category': getAmplitudeDeviceCategory(),
            // NOTE: This is a temporary user property until we fix the issue with not all split tests being sent to amplitude via the 'Split tests' user property.
            privacy_policy_default_agreement_split_test:
                contextData?.payload?.user_split_tests.find(
                    (item) => 'privacy_policy_default_agreement_test' in item
                )?.privacy_policy_default_agreement_test,
            business_details_reduction_split_test:
                contextData?.payload?.user_split_tests.find(
                    (item) => 'business_details_reduction' in item
                )?.business_details_reduction,
            open_banking_preference_split_test:
                contextData?.payload?.user_split_tests.find(
                    (item) => 'open_banking_preference' in item
                )?.open_banking_preference,
            monthly_revenue_question_split_test:
                contextData?.payload?.user_split_tests.find(
                    (item) => 'monthly_revenue_question' in item
                )?.monthly_revenue_question,
            'Split tests': getAmplitudeSplitTestsFromContext(
                contextData?.payload
            )
        });
    }

    if (userType) {
        tracking.ampli.identify(stateKey, {
            userType: userType
        });
    }

    const introducerID = applicationData?.payload?.introducer?.introducer_id;
    if (introducerID) {
        tracking.ampli.identify(stateKey, {
            customerType: 'introducer-referred'
        });
    } else {
        tracking.ampli.identify(stateKey, {
            customerType: 'direct'
        });
    }

    const requestedAmount =
        applicationData?.payload?.requested_products?.[0]?.amount;

    const suggestedAmount =
        applicationData?.payload?.requested_products?.[0]?.suggested_product
            ?.amount;

    const requestedProduct =
        applicationData?.payload?.requested_products?.[0]?.product_type;

    const suggestedProduct =
        applicationData?.payload?.requested_products?.[0]?.suggested_product
            ?.product_type;

    tracking.ampli.identify(stateKey, {
        [AMPLITUDE_USER_PROPERTIES.REQUESTED_AMOUNT]: requestedAmount,
        [AMPLITUDE_USER_PROPERTIES.SUGGESTED_AMOUNT]: suggestedAmount,
        [AMPLITUDE_USER_PROPERTIES.REQUESTED_PRODUCT]: requestedProduct,
        [AMPLITUDE_USER_PROPERTIES.SUGGESTED_PRODUCT]: suggestedProduct
    });

    const introducerCompanyName =
        applicationData?.payload?.introducer?.company?.name;
    if (introducerCompanyName) {
        tracking.ampli.identify(stateKey, {
            introducerAccount: introducerCompanyName
        });
    }
}

function* fetchAddressLookupLatest() {
    yield takeLatest('ADDRESSES_FETCH_REQUESTED', fetchAddressLookup);
}

function* fetchCompaniesLookupLatest() {
    yield takeLatest('COMPANIES_FETCH_REQUESTED', fetchCompaniesLookup);
}

function* fetchInitialStateLatest() {
    yield takeLatest(
        'APPLICATION_STATE_FETCH_REQUESTED',
        fetchInitialApplicationState
    );
}

function* fetchSaveNewCustomerState() {
    yield takeLatest('SAVE_NEW_CUSTOMER_STATE', saveNewCustomerState);
}

function* saveCustomerStateLatest() {
    yield takeLatest('SAVE_CUSTOMER_STATE_REQUESTED', saveCustomerState);
}

function* startFileUpload() {
    yield takeEvery('START_FILE_UPLOAD', uploadSaga);
}

function* checkAdditionalGuarantorsLatest() {
    yield takeLatest(
        'CHECK_ADDITIONAL_GUARANTORS_REQUESTED',
        checkAdditionalGuarantors
    );
}

export default function* sagas() {
    yield [
        fork(watchNavigation),
        fork(fetchInitialStateLatest),
        fork(fetchCompaniesLookupLatest),
        fork(fetchAddressLookupLatest),
        fork(saveCustomerStateLatest),
        fork(startFileUpload),
        fork(fetchIntercomAttributes),
        fork(checkAdditionalGuarantorsLatest),
        fork(fetchSaveNewCustomerState),
        ...additionalGuarantorSagas,
        ...automatedLendingDecisionSagas,
        ...checkEligibilitySagas,
        ...businessIntroductionDESagas,
        ...exitPollSagas,
        ...autoDirectorsSagas,
        ...monitoringSagas,
        ...modalsSagas,
        ...loanDecisionSagas,
        lendingAPISagas,
        ...accountCreatedSagas,
        ...iwTrackingSagas,
        ...SaveApplicationSagas,
        fork(requestALoginLinkWorker),
        ...ThreatMetrixSagas,
        ...BusinessIntroductionSagas,
        ...LoanRequestDetailsSagas,
        ...TrueLayerSagas,
        ...OffersSagas,
        ...TradingHistorySagas
    ];
}
